import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ArrowRight, ArrowLeft, CheckCircle, Sparkles, Search, HandHeart, Users, TrendingUp, Loader2, Plus } from 'lucide-react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

// 4-step intro tour shown once to first-time users. On finish/skip we call
// POST /api/settings/complete-onboarding so it never comes back.
const STEPS = [
  {
    icon: Sparkles,
    kicker: "Welcome to Neighborli",
    title: "Sharing tools, building community",
    body: "Neighborli is a peer-to-peer platform for lending, borrowing, and helping each other with projects — right in your neighborhood.",
    accent: "#004B00",
  },
  {
    icon: Search,
    kicker: "Discover",
    title: "Find tools & projects nearby",
    body: "Browse the Discover feed for tools your neighbors are willing to lend, projects that need a hand, and free giveaways. Search by title, category or owner to jump straight to what you need.",
    accent: "#004B00",
  },
  {
    icon: HandHeart,
    kicker: "Borrow with ease",
    title: "Request, pick up, return, rate",
    body: "Tap Request on any item to send a borrow request. Tell the owner when you need it and for how long. Once both parties agree, the loan goes active — and after return, lenders leave a quick rating.",
    accent: "#FF5500",
  },
  {
    icon: TrendingUp,
    kicker: "Neighborli status",
    title: "Grow from Newcomer to Icon",
    body: "Earn points by lending items, inviting friends to join Neighborli, and giving items away. As your points grow, so does your status — Newcomer, Apprentice, Contributor, all the way to Icon (shown on your Profile). Ready to start? List your first tool below and open it up to friends or your network.",
    accent: "#2E7D32",
  },
];

export default function OnboardingModal({ onClose, replay = false }) {
  const [step, setStep] = useState(0);
  const [finishing, setFinishing] = useState(false);
  const { refreshUser } = useAuth();
  const navigate = useNavigate();

  const isLast = step === STEPS.length - 1;
  const current = STEPS[step];
  const Icon = current.icon;

  const persistCompletion = async () => {
    // Replay mode skips the API call — the user has already completed the
    // tour once; we don't need to write the flag again.
    if (replay) return;
    try {
      await api.post('/settings/complete-onboarding');
      try { await refreshUser(); } catch { /* noop */ }
    } catch { /* best-effort */ }
  };

  const complete = async () => {
    setFinishing(true);
    await persistCompletion();
    onClose();
  };

  const completeAndListItem = async () => {
    setFinishing(true);
    await persistCompletion();
    onClose();
    // Deep-link to /feed with a query flag; FeedPage listens and opens Add Item.
    navigate('/feed?add=item');
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="onboarding-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5">
          <div className="flex gap-1.5">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${i === step ? 'w-8 bg-[#004B00]' : i < step ? 'w-4 bg-[#004B00]/60' : 'w-4 bg-stone-200'}`}
              />
            ))}
          </div>
          <button
            onClick={complete}
            data-testid="onboarding-skip"
            className="text-xs text-gray-400 hover:text-gray-600 font-nunito font-semibold flex items-center gap-1"
          >
            Skip <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="px-8 pt-10 pb-6 text-center">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: `${current.accent}15` }}
          >
            <Icon size={28} style={{ color: current.accent }} />
          </div>
          <p
            className="text-xs font-nunito font-bold uppercase tracking-widest mb-2"
            style={{ color: current.accent }}
          >
            {current.kicker}
          </p>
          <h2 className="text-2xl font-poppins font-semibold text-gray-900 mb-4" data-testid="onboarding-title">
            {current.title}
          </h2>
          <p className="text-sm font-nunito text-gray-600 leading-relaxed max-w-md mx-auto">
            {current.body}
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 pt-2 border-t border-stone-100">
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={() => setStep(s => Math.max(0, s - 1))}
              disabled={step === 0}
              data-testid="onboarding-back"
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 font-nunito font-semibold px-3 py-2 rounded-lg disabled:opacity-0 disabled:pointer-events-none transition-all"
            >
              <ArrowLeft size={14} /> Back
            </button>

            <span className="text-xs text-gray-400 font-nunito">Step {step + 1} of {STEPS.length}</span>

            {isLast ? (
              <button
                onClick={completeAndListItem}
                disabled={finishing}
                data-testid="onboarding-list-item"
                className="flex items-center gap-2 bg-[#FF5500] hover:bg-[#e04b00] text-white font-nunito font-semibold text-sm px-5 py-2.5 rounded-xl transition-all disabled:opacity-60 active:scale-95"
              >
                {finishing ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} strokeWidth={3} />}
                List an item
              </button>
            ) : (
              <button
                onClick={() => setStep(s => Math.min(STEPS.length - 1, s + 1))}
                data-testid="onboarding-next"
                className="flex items-center gap-2 bg-[#004B00] hover:bg-[#003800] text-white font-nunito font-semibold text-sm px-5 py-2.5 rounded-xl transition-all active:scale-95"
              >
                Next <ArrowRight size={14} />
              </button>
            )}
          </div>

          {/* Secondary "Maybe later" on the last step so users aren't forced to add an item. */}
          {isLast && (
            <div className="mt-3 text-center">
              <button
                type="button"
                onClick={complete}
                disabled={finishing}
                data-testid="onboarding-finish"
                className="text-xs text-gray-400 hover:text-gray-600 font-nunito font-semibold disabled:opacity-60"
              >
                Maybe later — just let me browse
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
